import { useMemo, useState } from "react";
import { History, Download, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { grondstofAfboekingen, hulpstofAfboekingen } from "@/lib/voorraadHistory";

const N = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const isoWeek = (d) => {
  const dt = new Date(d + "T00:00:00"); if (isNaN(dt)) return "";
  const t = new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
  const day = t.getUTCDay() || 7; t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const wk = Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
};

// Fase 3: dagoverzicht (laatste 4 weken) van af- en bijboekingen loods ⇄ productie + wekelijkse PDF.
// Puur afgeleid uit bestaande data (entries/halffabRuns/afkeur/expeditie/hulpstofInkoop) — leest alleen, gaat mee in back-ups.
export default function AfboekHistorie({ xl }) {
  const { rawMaterials, helpers, entries, halffabRuns, afkeur, expeditie, hulpstofInkoop, kgf, jsPDF, autoTable } = xl;
  const [cat, setCat] = useState("Alle");
  const [richting, setRichting] = useState("Alle");

  const minDate = useMemo(() => new Date(Date.now() - 28 * 86400000).toISOString().slice(0, 10), []);

  const movements = useMemo(() => {
    const out = [];
    (rawMaterials || []).forEach((m) => grondstofAfboekingen(m.naam, entries, halffabRuns, afkeur, expeditie).forEach((r) => out.push({ ...r, naam: m.naam, categorie: "Grondstof", richting: "uit" })));
    (helpers || []).forEach((h) => hulpstofAfboekingen(h.naam, entries, halffabRuns, afkeur, hulpstofInkoop).forEach((r) => out.push({ ...r, naam: h.naam, categorie: "Hulpstof", richting: "uit" })));
    (expeditie || []).filter((b) => b && (!b.type || b.type === "Grondstof") && N(b.kg) > 0).forEach((b) => out.push({ datum: b.datum || "", naam: b.grondstof || "", categorie: "Grondstof", richting: "in", kg: N(b.kg), bron: b.leverancier || "Ontvangst", detail: "ontvangst (loods)", tracecode: String(b.eigenTracecode || b.batchnr || "").trim(), door: b.door || "" }));
    (hulpstofInkoop || []).filter((b) => b && N(b.kg) > 0).forEach((b) => out.push({ datum: b.datum || "", naam: b.hulpstof || "", categorie: "Hulpstof", richting: "in", kg: N(b.kg), bron: b.leverancier || "Ontvangst", detail: "ontvangst (loods)", tracecode: String(b.eigenTracecode || b.batchnr || "").trim(), door: b.door || "" }));
    return out
      .filter((r) => r.datum && r.datum >= minDate && N(r.kg) > 0.0001)
      .filter((r) => cat === "Alle" || r.categorie === cat)
      .filter((r) => richting === "Alle" || r.richting === richting)
      .sort((a, b) => String(b.datum).localeCompare(String(a.datum)) || String(a.richting).localeCompare(String(b.richting)));
  }, [rawMaterials, helpers, entries, halffabRuns, afkeur, expeditie, hulpstofInkoop, minDate, cat, richting]);

  const perDag = useMemo(() => {
    const m = new Map();
    movements.forEach((r) => { if (!m.has(r.datum)) m.set(r.datum, []); m.get(r.datum).push(r); });
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [movements]);

  const weken = useMemo(() => [...new Set(movements.map((r) => isoWeek(r.datum)).filter(Boolean))].sort().reverse(), [movements]);

  const downloadWeekPdf = (wk) => {
    const rows = movements.filter((r) => isoWeek(r.datum) === wk).sort((a, b) => String(a.datum).localeCompare(String(b.datum)));
    const dts = rows.map((r) => r.datum).sort();
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFontSize(14); doc.setTextColor(28, 100, 176);
    doc.text(`Af-/bijboekingen — ${wk}`, 14, 14);
    doc.setFontSize(9); doc.setTextColor(90);
    doc.text(`Periode: ${dts[0] || "—"} t/m ${dts[dts.length - 1] || "—"} · ${rows.length} boekingen · gegenereerd ${new Date().toLocaleString("nl-NL")}`, 14, 20);
    autoTable(doc, {
      startY: 24, styles: { fontSize: 8, cellPadding: 1.4 }, headStyles: { fillColor: [28, 100, 176] },
      head: [["Datum", "Richting", "Categorie", "Artikel", "kg", "Tracecode", "Bron / reden", "Wie"]],
      body: rows.map((r) => [r.datum, r.richting === "in" ? "BIJ (in)" : "AF (uit)", r.categorie, r.naam, N(r.kg).toLocaleString("nl-NL", { maximumFractionDigits: 1 }), r.tracecode || "—", `${r.bron || ""}${r.detail ? ` · ${r.detail}` : ""}`.trim() || "—", r.door || "—"]),
    });
    doc.save(`afboekingen-${wk}.pdf`);
  };

  const sel = "rounded border border-slate-300 px-2 py-1 text-sm";

  return (
    <details className="mb-6 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="afboek-historie">
      <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3" data-testid="afboek-historie-toggle">
        <span className="flex items-center gap-2 text-lg font-bold text-slate-900"><History className="h-5 w-5 text-slate-600" />Afboekhistorie — dagoverzicht (laatste 4 weken)</span>
        <span className="text-sm font-medium text-slate-600">{movements.length} boekingen · openklappen</span>
      </summary>

      <div className="mt-3">
        <p className="mb-3 max-w-4xl text-sm text-slate-600">Elke af- en bijboeking van de loods naar/uit de productie, per dag, over de laatste 4 weken. Download per week een PDF om op de computer te bewaren (wie / wat / wanneer). Puur afgeleid uit je bestaande registraties; gaat mee in de back-ups.</p>

        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="flex flex-col text-xs font-semibold text-slate-600">Categorie
            <select className={sel} data-testid="afboek-cat" value={cat} onChange={(e) => setCat(e.target.value)}><option>Alle</option><option>Grondstof</option><option>Hulpstof</option></select>
          </label>
          <label className="flex flex-col text-xs font-semibold text-slate-600">Richting
            <select className={sel} data-testid="afboek-richting" value={richting} onChange={(e) => setRichting(e.target.value)}><option value="Alle">Alle</option><option value="uit">Alleen afboekingen (uit)</option><option value="in">Alleen bijboekingen (in)</option></select>
          </label>
          {weken.length > 0 && (
            <label className="flex flex-col text-xs font-semibold text-slate-600">Download week-PDF
              <select className={sel} data-testid="afboek-week-pdf" defaultValue="" onChange={(e) => { if (e.target.value) { downloadWeekPdf(e.target.value); e.target.value = ""; } }}>
                <option value="">— kies week —</option>
                {weken.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </label>
          )}
        </div>

        {perDag.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white px-3 py-6 text-center text-slate-400" data-testid="afboek-leeg">Geen boekingen in de laatste 4 weken.</div>
        ) : perDag.map(([datum, rows]) => (
          <div key={datum} className="mb-3 overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="afboek-dag">
            <div className="flex items-center justify-between bg-slate-100 px-3 py-1.5 text-sm font-bold text-slate-700">
              <span>{datum} · {isoWeek(datum)}</span>
              <span className="text-xs font-normal text-slate-500">{rows.length} boeking{rows.length === 1 ? "" : "en"}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100" data-testid="afboek-rij">
                    <td className="px-3 py-1.5 w-10">{r.richting === "in" ? <ArrowDownLeft className="h-4 w-4 text-emerald-600" /> : <ArrowUpRight className="h-4 w-4 text-rose-500" />}</td>
                    <td className="px-2 py-1.5"><span className="rounded bg-slate-100 px-1.5 text-xs text-slate-500">{r.categorie}</span></td>
                    <td className="px-2 py-1.5 font-medium">{r.naam}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{r.richting === "in" ? "+" : "−"}{kgf(N(r.kg))}</td>
                    <td className="px-2 py-1.5 font-mono text-xs text-slate-500">{r.tracecode || "—"}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-600">{r.bron}{r.detail ? ` · ${r.detail}` : ""}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-400">{r.door || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </details>
  );
}
